import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid } from "recharts";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPct, fmtPeriod } from "./theme";
import { SkeletonCard, NoData, SourceChip, SectionTitle, SrTable,
  DeltaBadge, ChartTooltipContent } from "./components";
import { regionName, ORIGIN_NAMES_IT } from "./regionNames";
import { pulseGet } from "./api";

const Toggle = ({ options, value, onChange, dm, testid }) => {
  const T = tokens(dm);
  return (
    <div className="inline-flex" role="group" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)}
          data-testid={`${testid}-${o.value}`} aria-pressed={value === o.value}
          className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5"
          style={{
            background: value === o.value ? T.brand : T.surface,
            color: value === o.value ? "#fff" : T.muted,
            border: `1px solid ${value === o.value ? T.brand : T.border}`,
          }}>
          {o.label}
        </button>
      ))}
    </div>
  );
};

/** P2 — "Origins × spending", REDESIGNED as two DECLARED independent views
 *  (owner decision): national spending by origin country + spending by
 *  visited region (all origins). The joint origin×region cross is NOT
 *  published by Banca d'Italia and is never derived. */
export default function ItalySpendPanel({ dm }) {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [view, setView] = useState("origin");
  const [freq, setFreq] = useState("Q");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true);
    pulseGet("italy/spend", { view, freq })
      .then((d) => { if (on) setData(d); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [view, freq]);

  const displayName = (r) => {
    if (view === "region") return regionName(r.code, lang, r.label);
    return lang === "it" ? (ORIGIN_NAMES_IT[r.code] || r.label) : r.label;
  };

  const bars = useMemo(() => (data?.rows || []).slice(0, 10).map((r) => ({
    name: displayName(r), value: r.latest_value, yoy: r.yoy_pct,
  })), [data, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalSeries = useMemo(() => (data?.total?.series || []).map((p) => ({
    period: fmtPeriod(p.period, lang), value: p.value,
  })), [data, lang]);

  const fmtEur = (v) => `€${fmtCompact(v === null || v === undefined ? null : v * 1e6, lang)}`;

  return (
    <section className="mb-10" data-testid="italy-spend-panel" aria-labelledby="italy-p2-title">
      <SectionTitle dm={dm} right={
        <div className="flex items-center gap-2 flex-wrap">
          <Toggle dm={dm} testid="italy-spend-view" value={view} onChange={setView}
            options={[{ value: "origin", label: t("it_p2_view_origin") },
                      { value: "region", label: t("it_p2_view_region") }]} />
          <Toggle dm={dm} testid="italy-spend-freq" value={freq} onChange={setFreq}
            options={[{ value: "Q", label: t("it_p2_freq_q") },
                      { value: "A", label: t("it_p2_freq_a") }]} />
        </div>
      }>
        <span id="italy-p2-title">{t("it_p2_title")}</span>
      </SectionTitle>
      <p className="text-xs mb-3 max-w-3xl" style={{ color: T.muted }}>
        {view === "origin" ? t("it_p2_origin_desc") : t("it_p2_region_desc")}
      </p>

      {loading && <SkeletonCard h={320} dm={dm} />}
      {!loading && !(data?.rows || []).length && <NoData dm={dm} h={160} testid="italy-spend-no-data" />}
      {!loading && (data?.rows || []).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }}
            data-testid="italy-spend-bars">
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: T.muted }}>
              {t("it_p2_latest", { period: fmtPeriod(data.latest_period, lang) })}
            </p>
            <div style={{ height: Math.max(240, bars.length * 30) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bars} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
                  <CartesianGrid stroke={T.border} horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => fmtEur(v)}
                    tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={{ stroke: T.border }} />
                  <YAxis type="category" dataKey="name" width={120}
                    tick={{ fontSize: 10, fill: T.text }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTooltipContent dm={dm} formatter={(v) => fmtEur(v)} />} />
                  <Bar dataKey="value" name={t("it_p2_spend")} fill={T.brand}
                    isAnimationActive animationDuration={400} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <SrTable caption={t("it_p2_title")}
              columns={[view === "origin" ? t("it_p2_view_origin") : t("it_p2_view_region"),
                        t("it_p2_spend"), t("yoy"), t("it_p2_share")]}
              rows={(data.rows || []).map((r) => [displayName(r), fmtEur(r.latest_value),
                fmtPct(r.yoy_pct, lang), fmtPct(r.share_pct, lang, false)])} />
          </div>

          <div className="flex flex-col gap-4">
            {data.total && (
              <div className="p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }}
                data-testid="italy-spend-total">
                <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: T.muted }}>
                  {t("it_p2_total", { period: fmtPeriod(data.latest_period, lang) })}
                </p>
                <div className="flex items-baseline gap-2 flex-wrap mb-2">
                  <span className="pulse-num text-3xl font-semibold">{fmtEur(data.total.latest_value)}</span>
                  <DeltaBadge pct={data.total.yoy_pct} dm={dm} />
                </div>
                {totalSeries.length > 1 && (
                  <div style={{ height: 140 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={totalSeries} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                        <XAxis dataKey="period" tick={{ fontSize: 9, fill: T.muted }}
                          interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: T.border }} />
                        <YAxis hide />
                        <Tooltip content={<ChartTooltipContent dm={dm} formatter={(v) => fmtEur(v)} />} />
                        <Line type="monotone" dataKey="value" name={t("it_p2_spend")}
                          stroke={T.brand} strokeWidth={2} dot={false} isAnimationActive animationDuration={400} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <SrTable caption={t("it_p2_total", { period: fmtPeriod(data.latest_period, lang) })}
                  columns={[t("rank_col_year"), t("it_p2_spend")]}
                  rows={totalSeries.map((p) => [p.period, fmtEur(p.value)])} />
              </div>
            )}
            <div className="p-4" style={{ background: T.surface2, border: `1px dashed ${T.border}` }}>
              <p className="text-[11px] leading-relaxed" style={{ color: T.muted }}
                data-testid="italy-spend-cross-note">
                <strong className="uppercase">{t("it_p2_note_label")}</strong> — {t("it_p2_cross_note")}
              </p>
            </div>
            <SourceChip source="BANCA_D_ITALIA" dataset="turismo_tavole"
              period={data.latest_period} dm={dm} testid="italy-spend-source" />
          </div>
        </div>
      )}
    </section>
  );
}
